<template>
  <main class="op-page">
    <!-- Header -->
    <header class="op-header">
      <div>
        <div class="op-header__date">{{ todayLabel }}</div>
        <div class="op-header__operator">
          <i class="fa-solid fa-user-nurse"></i> {{ operatorName }}
        </div>
      </div>
      <button class="op-refresh" :disabled="loading" @click="refresh">
        <i
          :class="
            loading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-rotate'
          "
        ></i>
      </button>
    </header>

    <!-- Selettore struttura -->
    <div class="op-facility">
      <select
        v-model.number="facilityId"
        class="op-input"
        @change="onFacilityChange"
      >
        <option :value="null" disabled>Seleziona struttura…</option>
        <option v-for="f in facilities" :key="f.id" :value="f.id">
          {{ f.name }}
        </option>
      </select>
    </div>

    <div v-if="loading" class="op-state">Caricamento…</div>

    <!-- ===================== LISTA OSPITI ===================== -->
    <template v-else-if="!selectedGuest && summary">
      <div class="op-stats">
        <div class="op-stat">
          <span class="op-stat__v">{{ summary.stats.completedToday }}</span>
          <span class="op-stat__l">completate oggi</span>
        </div>
        <div class="op-stat">
          <span class="op-stat__v">{{ summary.stats.toVerifyToday }}</span>
          <span class="op-stat__l">da verificare</span>
        </div>
        <div class="op-stat">
          <span class="op-stat__v">{{
            summary.stats.medicationsNotDoneToday
          }}</span>
          <span class="op-stat__l">farmaci non somm.</span>
        </div>
        <div class="op-stat">
          <span class="op-stat__v">{{ summary.stats.vitalsToday }}</span>
          <span class="op-stat__l">rilevazioni</span>
        </div>
      </div>

      <div v-if="!summary.guests.length" class="op-state">
        Nessun ospite attivo in questa struttura.
      </div>
      <div class="op-guests">
        <button
          v-for="g in summary.guests"
          :key="g.id"
          class="op-guest-card"
          @click="openGuest(g.id)"
        >
          <div class="op-guest-card__main">
            <div class="op-guest-card__name">
              {{ g.lastName }} {{ g.firstName }}
            </div>
            <div class="op-guest-card__bed">
              <i class="fa-solid fa-bed"></i>
              {{ g.roomName || "—"
              }}<span v-if="g.bedCode"> · {{ g.bedCode }}</span>
            </div>
          </div>
          <span v-if="g.activityCountToday" class="op-badge">
            {{ g.activityCountToday }}
          </span>
          <i class="fa-solid fa-chevron-right op-guest-card__chev"></i>
        </button>
      </div>
    </template>

    <!-- ===================== SCHEDA RAPIDA OSPITE ===================== -->
    <template v-else-if="selectedGuest && guestView">
      <button class="op-back" @click="closeGuest">
        <i class="fa-solid fa-arrow-left"></i> Tutti gli ospiti
      </button>

      <div class="op-guest-head">
        <h2 class="op-guest-head__name">
          {{ guestView.guest.lastName }} {{ guestView.guest.firstName }}
        </h2>
        <div class="op-guest-head__bed">
          <i class="fa-solid fa-bed"></i>
          {{ guestView.guest.roomName || "Nessuna camera" }}
          <span v-if="guestView.guest.bedCode">
            · {{ guestView.guest.bedCode }}</span
          >
        </div>
      </div>

      <!-- Azioni rapide -->
      <h3 class="op-section-title">Azioni rapide</h3>
      <div class="op-actions-grid">
        <button
          v-for="d in actionDefs"
          :key="d.type"
          class="op-action"
          @click="openAction(d)"
        >
          <i :class="d.icon" class="op-action__icon"></i>
          <span class="op-action__label">{{ d.label }}</span>
        </button>
      </div>

      <!-- Ultime rilevazioni -->
      <h3 class="op-section-title">Ultime rilevazioni</h3>
      <div class="op-vitals">
        <div class="op-vital">
          <span class="op-vital__l">Glicemia</span>
          <span class="op-vital__v">{{ vital("blood_glucose") }}</span>
        </div>
        <div class="op-vital">
          <span class="op-vital__l">Pressione</span>
          <span class="op-vital__v">{{ vital("blood_pressure") }}</span>
        </div>
        <div class="op-vital">
          <span class="op-vital__l">Saturazione</span>
          <span class="op-vital__v">{{ vital("oxygen_saturation") }}</span>
        </div>
      </div>

      <!-- Terapie del giorno (read-only) -->
      <h3 class="op-section-title">Terapie del giorno</h3>
      <div v-if="!guestView.therapies.length" class="op-state op-state--sm">
        Nessuna terapia attiva.
      </div>
      <ul v-else class="op-therapies">
        <li v-for="t in guestView.therapies" :key="t.therapyId">
          <strong>{{ t.scheduledTime }}</strong> — {{ t.medicationName }}
          <small>({{ t.doseAmount }} {{ t.doseUnit }})</small>
        </li>
      </ul>

      <!-- Ultime attività -->
      <h3 class="op-section-title">Ultime attività</h3>
      <div
        v-if="!guestView.recentActivities.length"
        class="op-state op-state--sm"
      >
        Nessuna attività registrata.
      </div>
      <ul v-else class="op-timeline">
        <li
          v-for="a in guestView.recentActivities"
          :key="a.id"
          class="op-event"
        >
          <div class="op-event__time">{{ time(a.performedAt) }}</div>
          <div class="op-event__body">
            <span class="op-event__label">{{
              a.actionLabel || a.actionType
            }}</span>
            <span :class="['op-event__status', 'st-' + a.status]">
              {{ statusLabel(a.status) }}
            </span>
            <div v-if="value(a)" class="op-event__value">{{ value(a) }}</div>
            <div v-if="a.notes" class="op-event__notes">{{ a.notes }}</div>
          </div>
        </li>
      </ul>
    </template>

    <!-- Modale azione -->
    <OperatorActionModal
      v-if="activeAction"
      :def="activeAction"
      :guest-id="selectedGuest"
      :facility-id="facilityId"
      :therapies="guestView ? guestView.therapies : []"
      :absorbent-pads="guestView ? guestView.absorbentPads || [] : []"
      :bed-pads="guestView ? guestView.bedPads || [] : []"
      @close="activeAction = null"
      @saved="onActionSaved"
    />
  </main>
</template>

<script>
import { mapActions } from "vuex";
import OperatorActionModal from "@/components/OperatorActionModal.vue";
import {
  ACTION_DEFS,
  statusLabel,
  formatActivityValue,
} from "@/utils/careActions";

export default {
  name: "OperatorPanelPage",
  components: { OperatorActionModal },
  data() {
    return {
      facilities: [],
      facilityId: null,
      summary: null,
      selectedGuest: null,
      guestView: null,
      loading: false,
      actionDefs: ACTION_DEFS,
      activeAction: null,
    };
  },
  computed: {
    operatorName() {
      const u = this.$store.state.currentUser;
      return u ? u.operatorName || u.email || "Operatore" : "Operatore";
    },
    todayLabel() {
      return new Date().toLocaleDateString("it-IT", {
        weekday: "long",
        day: "numeric",
        month: "long",
      });
    },
  },
  methods: {
    ...mapActions([
      "fetchFacilities",
      "fetchOperatorSummary",
      "fetchOperatorGuestView",
    ]),
    statusLabel,
    value(a) {
      return formatActivityValue(a);
    },
    vital(type) {
      const v = this.guestView?.latestVitals?.[type];
      if (!v) return "—";
      return `${formatActivityValue(v)} · ${this.time(v.performedAt)}`;
    },
    time(d) {
      if (!d) return "";
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return "";
      const p = (n) => String(n).padStart(2, "0");
      return `${p(dt.getHours())}:${p(dt.getMinutes())}`;
    },
    async loadSummary() {
      if (!this.facilityId) return;
      this.loading = true;
      try {
        this.summary = await this.fetchOperatorSummary({
          facilityId: this.facilityId,
        });
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Caricamento pannello non riuscito."
        );
      } finally {
        this.loading = false;
      }
    },
    async loadGuest(guestId) {
      this.loading = true;
      try {
        this.guestView = await this.fetchOperatorGuestView({
          guestId,
          facilityId: this.facilityId,
        });
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Caricamento ospite non riuscito."
        );
        this.selectedGuest = null;
      } finally {
        this.loading = false;
      }
    },
    onFacilityChange() {
      this.selectedGuest = null;
      this.guestView = null;
      this.loadSummary();
    },
    openGuest(id) {
      this.selectedGuest = id;
      this.guestView = null;
      this.loadGuest(id);
    },
    closeGuest() {
      this.selectedGuest = null;
      this.guestView = null;
      this.loadSummary();
    },
    openAction(def) {
      this.activeAction = def;
    },
    async onActionSaved() {
      this.activeAction = null;
      await this.loadGuest(this.selectedGuest);
    },
    refresh() {
      if (this.selectedGuest) this.loadGuest(this.selectedGuest);
      else this.loadSummary();
    },
  },
  async mounted() {
    try {
      this.facilities = await this.fetchFacilities();
      if (this.facilities.length === 1) {
        this.facilityId = this.facilities[0].id;
        await this.loadSummary();
      }
    } catch {
      this.facilities = [];
    }
  },
};
</script>

<style scoped>
.op-page {
  max-width: 640px;
  margin: 0 auto;
  padding: 12px 12px 60px;
  color: #0f172a;
}
.op-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.op-header__date {
  font-size: 13px;
  color: #64748b;
  text-transform: capitalize;
}
.op-header__operator {
  font-weight: 700;
  color: var(--villa-navy, #082b57);
}
.op-refresh {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid var(--villa-azure, #dceaf7);
  background: #fff;
  font-size: 18px;
  color: var(--villa-blue, #2f6fae);
  cursor: pointer;
}
.op-facility {
  margin-bottom: 14px;
}
.op-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 12px;
  font-size: 16px;
  background: #fff;
}
.op-state {
  text-align: center;
  color: #64748b;
  padding: 28px 12px;
}
.op-state--sm {
  padding: 10px;
  text-align: left;
  font-size: 14px;
}
.op-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 14px;
}
.op-stat {
  background: #fff;
  border: 1px solid var(--villa-azure, #dceaf7);
  border-radius: 12px;
  padding: 10px 6px;
  text-align: center;
}
.op-stat__v {
  display: block;
  font-size: 22px;
  font-weight: 800;
  color: var(--villa-navy, #082b57);
}
.op-stat__l {
  font-size: 11px;
  color: #64748b;
}
.op-guests {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.op-guest-card {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid var(--villa-azure, #dceaf7);
  border-radius: 14px;
  padding: 16px;
  cursor: pointer;
}
.op-guest-card__main {
  flex: 1;
}
.op-guest-card__name {
  font-size: 17px;
  font-weight: 700;
  color: var(--villa-navy, #082b57);
}
.op-guest-card__bed {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}
.op-guest-card__chev {
  color: #cbd5e1;
}
.op-badge {
  background: var(--villa-blue, #2f6fae);
  color: #fff;
  border-radius: 999px;
  min-width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  padding: 0 7px;
}
.op-back {
  background: none;
  border: none;
  color: var(--villa-blue, #2f6fae);
  font-size: 15px;
  cursor: pointer;
  padding: 6px 0;
  margin-bottom: 6px;
}
.op-guest-head {
  margin-bottom: 16px;
}
.op-guest-head__name {
  margin: 0;
  color: var(--villa-navy, #082b57);
}
.op-guest-head__bed {
  color: #64748b;
  font-size: 14px;
  margin-top: 2px;
}
.op-section-title {
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin: 20px 0 10px;
}
.op-actions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.op-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid var(--villa-azure, #dceaf7);
  border-radius: 14px;
  padding: 16px 6px;
  cursor: pointer;
}
.op-action__icon {
  font-size: 24px;
  color: var(--villa-blue, #2f6fae);
}
.op-action__label {
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  color: #0f172a;
}
.op-vitals {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.op-vital {
  background: #fff;
  border: 1px solid var(--villa-azure, #dceaf7);
  border-radius: 12px;
  padding: 10px;
}
.op-vital__l {
  display: block;
  font-size: 11px;
  color: #64748b;
}
.op-vital__v {
  font-size: 13px;
  font-weight: 600;
}
.op-therapies {
  list-style: none;
  margin: 0;
  padding: 0;
}
.op-therapies li {
  background: #fff;
  border: 1px solid var(--villa-azure, #dceaf7);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 8px;
  font-size: 14px;
}
.op-timeline {
  list-style: none;
  margin: 0;
  padding: 0;
}
.op-event {
  display: flex;
  gap: 12px;
  background: #fff;
  border: 1px solid var(--villa-azure, #dceaf7);
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 8px;
}
.op-event__time {
  font-weight: 700;
  color: var(--villa-blue, #2f6fae);
  font-size: 14px;
  min-width: 44px;
}
.op-event__label {
  font-weight: 600;
}
.op-event__status {
  margin-left: 8px;
  font-size: 12px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
}
.op-event__status.st-completed {
  background: #dcfce7;
  color: #166534;
}
.op-event__status.st-refused,
.op-event__status.st-not_done {
  background: #fee2e2;
  color: #991b1b;
}
.op-event__status.st-to_verify,
.op-event__status.st-partial {
  background: #fef9c3;
  color: #854d0e;
}
.op-event__value {
  font-size: 14px;
  margin-top: 2px;
}
.op-event__notes {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}
</style>
